import { Button, Grid, Stack, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
  useCreateMedComplexCertificateMutation,
  useCreateMedComplexFilesMutation,
  useDeleteMedComplexFilesMutation,
  useLazyGetFileQuery,
  useUpdateMedComplexCertificateMutation,
} from '@/api'
import { DateTimePicker, DrawerBox, Input } from '@/components'
import { useDrawer, useModal } from '@/hooks'
import { DrawerType, ModalType } from '@/store/app'
import { styles } from './styles'
import { formatDate, getDateInUtcMode } from '@/utils/helpers'
import { MedComplexContext } from '../../../../MedComplex'
import { FileDocument } from '@/modules/MedComplex/modules'
import type { CreateDocumentProps } from './types'
import dayjs from 'dayjs'
import { MedComplexCertificate } from '@/api/models/medcomplex'

export const CreateDocument = (props: CreateDocumentProps) => {
  const { createSignal, certificate, setCertificate } = props
  const { hideDrawer, drawerOpened } = useDrawer()
  const { showModal } = useModal()
  const { medcomplex, refetch } = useContext(MedComplexContext)
  const methods = useForm()
  const watchMethods = methods.watch([
    'date_inspection',
    'date_expiration',
    'serial_number',
    'files',
  ])
  const [getFile] = useLazyGetFileQuery()
  const [deletedFiles, setDeletedFiles] = useState<number[]>([])
  const [newFiles, setNewFiles] = useState<File[]>([])

  const defaults = useMemo(
    async () => ({
      date_inspection: certificate
        ? dayjs(certificate?.date_inspection)
        : undefined,
      date_expiration: certificate
        ? dayjs(certificate?.date_expiration)
        : undefined,
      serial_number: certificate?.serial_number,
      files: certificate?.files
        ? await Promise.all(
            certificate?.files?.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              async (file: any) => await getFile(file.url)
            )
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ).then((res: any) => {
            return res.map(
              (file: BlobPart, i: number) =>
                new File(
                  [file],
                  certificate?.files ? certificate?.files[i]?.ext_file_id : '',
                  {
                    type: 'application/pdf',
                  }
                )
            )
          })
        : [],
    }),
    [certificate]
  )

  useMemo(async () => {
    if (certificate !== null) {
      methods.reset(await defaults)
    } else {
      methods.reset({
        date_inspection: undefined,
        date_expiration: undefined,
        serial_number: undefined,
        files: undefined,
      })
    }
  }, [certificate])

  const [createCertificate] = useCreateMedComplexCertificateMutation()
  const [createCertificateFiles] = useCreateMedComplexFilesMutation()
  const [updateCertificate] = useUpdateMedComplexCertificateMutation()
  const [deleteCertificateFiles] = useDeleteMedComplexFilesMutation()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (value: any) => {
    if (
      medcomplex?.id &&
      value.date_inspection &&
      value.date_expiration &&
      value.serial_number
    ) {
      if (certificate) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          date_inspection: formatDate(new Date(value.date_inspection)),
          date_expiration: formatDate(
            dayjs(
              dayjs(value.date_expiration).format('YYYY-MM-DD') +
                dayjs().format('HH:mm:ss')
            ).utc()
          ),
          serial_number: value.serial_number,
        }
        Object.keys(payload).forEach((e) => {
          if (JSON.stringify(value[e]) === JSON.stringify(certificate[e])) {
            delete payload[e]
          }
        })
        const deletedFilesIds = certificate?.files
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ?.map((file: any, index: number) =>
            deletedFiles.includes(index) ? file.id : -1
          )
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ?.filter((e: any) => e !== -1)
        // console.log('payload:', payload)
        // console.log('delete files ids:', deletedFilesIds)
        // console.log('upload files:', newFiles)
        await updateCertificate({
          id: medcomplex?.id,
          certId: certificate?.id,
          payload: payload,
        })
        await deleteCertificateFiles({
          id: medcomplex?.id,
          certId: certificate?.id,
          payload: {
            ids: deletedFilesIds,
          },
        })
        if (newFiles.length > 0) {
          const formData = new FormData()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          newFiles.forEach((item: any) => {
            formData.append('files', item)
          })
          createCertificateFiles({
            id: medcomplex?.id,
            certId: certificate?.id,
            payload: formData,
          })
        }
        //
        setDeletedFiles([])
        setNewFiles([])
      } else {
        const certificate = {
          date_inspection: formatDate(new Date(value.date_inspection)),
          date_expiration: formatDate(
            dayjs(
              dayjs(value.date_expiration).format('YYYY-MM-DD') +
                dayjs().format('HH:mm:ss')
            ).utc()
          ),
          serial_number: value.serial_number,
        }
        await createCertificate({
          id: medcomplex?.id,
          payload: certificate,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).then((response: any) => {
          if (response?.data?.id && value.files?.length > 0) {
            const formData = new FormData()
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value.files.forEach((item: any) => {
              formData.append('files', item)
            })
            createCertificateFiles({
              id: medcomplex?.id,
              certId: response?.data?.id,
              payload: formData,
            })
          }
          showModal(ModalType.MEDCOMPLEX_COMPLETE_CERTIFICATE)
          createSignal && createSignal(response?.data?.id)
        })
      }
      hideDrawer()
      setCertificate && setCertificate(null)
      refetch()
    }
  }

  return (
    <DrawerBox
      onClose={hideDrawer}
      opened={
        DrawerType.CREATE_MEDCOMPLEX__CREATE_VERIFICATION_DOCUMENT ===
        drawerOpened
      }
      bottom={
        <Stack gap={1.5}>
          <Button
            onClick={methods.handleSubmit(onSubmit)}
            variant={'contained'}
            //color="info"
            type={'submit'}
            disabled={
              !(
                medcomplex?.id &&
                watchMethods[0] &&
                watchMethods[1] &&
                watchMethods[2]
              )
            }
          >
            Сохранить
          </Button>
          <Button variant={'contained'} color={'primary'}>
            Отмена
          </Button>
        </Stack>
      }
      title={'Карточка документа'}
    >
      <FormProvider {...methods}>
        <form>
          <Grid spacing={3} container>
            <Grid item sm={12}>
              <Typography mb={1} sx={styles.control.label}>
                Поля для обязательного заполнения *
              </Typography>
              <Stack gap={2}>
                <DateTimePicker
                  format={'DD.MM.YYYY'}
                  views={['year', 'month', 'day']}
                  label={'Дата поверки *'}
                  slotProps={{
                    textField: {
                      //size: 'small',
                      fullWidth: true,
                      hiddenLabel: false,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      textTransform: 'lowercase',
                    },
                  }}
                  disableFuture={true}
                  name="date_inspection"
                />
                <DateTimePicker
                  format={'DD.MM.YYYY'}
                  views={['year', 'month', 'day']}
                  label={'Дата истечения поверки *'}
                  slotProps={{
                    textField: {
                      //size: 'small',
                      fullWidth: true,
                      hiddenLabel: false,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      textTransform: 'lowercase',
                    },
                  }}
                  disablePast={true}
                  name="date_expiration"
                />
                <Input
                  fullWidth
                  name="serial_number"
                  //placeholder="000000000"
                  placeholder="Введите серийный номер документа"
                  label="Серийный номер документа*"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    //maxLength: 9,
                  }}
                />
              </Stack>
            </Grid>
            <Grid item sm={12}>
              <FileDocument
                setDeletedFiles={setDeletedFiles}
                setNewFiles={setNewFiles}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </DrawerBox>
  )
}
